import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './FactoryAssessment.css';

const steps = [
  {
    number: '01',
    icon: '📅',
    title: 'Schedule a Visit',
    desc: 'Contact us to arrange a convenient time for our automation experts to visit your facility.',
  },
  {
    number: '02',
    icon: '🔍',
    title: 'On-Site Assessment',
    desc: 'Our engineers walk your production floor to identify automation opportunities, inefficiencies, and quick wins.',
  },
  {
    number: '03',
    icon: '📊',
    title: 'Detailed Analysis Report',
    desc: 'Receive a comprehensive report covering findings, automation potential, expected ROI, and technology recommendations.',
  },
  {
    number: '04',
    icon: '🗺️',
    title: 'Custom Plan & Estimate',
    desc: 'We deliver a phased implementation roadmap with transparent cost estimates — zero obligation to proceed.',
  },
];

const FactoryAssessment = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: [0.25, 0.4, 0.25, 1] },
    },
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="factory-assessment" className="factory-assessment" ref={ref}>
      <div className="fa-container">
        {/* Header */}
        <motion.div
          className="fa-header"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          <motion.h2 className="fa-title" variants={itemVariants}>
            Unlock Your Factory's Full Potential —{' '}
            <span className="fa-highlight">Absolutely FREE</span>
          </motion.h2>

          <motion.p className="fa-subtitle" variants={itemVariants}>
            Our automation experts visit your facility, assess every process for improvement
            opportunities, and deliver a detailed report with a custom roadmap and transparent
            cost estimate. No commitment. No cost. Just insight.
          </motion.p>
        </motion.div>

        {/* Steps */}
        <motion.div
          className="fa-steps"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          {steps.map((step, i) => (
            <motion.div key={i} className="fa-step-card" variants={itemVariants}>
              <div className="fa-step-number">{step.number}</div>
              <div className="fa-step-icon">{step.icon}</div>
              <h3 className="fa-step-title">{step.title}</h3>
              <p className="fa-step-desc">{step.desc}</p>
              {i < steps.length - 1 && <div className="fa-step-arrow">→</div>}
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Row */}
        <motion.div
          className="fa-cta-row"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          <motion.div className="fa-cta-card" variants={itemVariants}>
            <div className="fa-cta-icon">📞</div>
            <div className="fa-cta-info">
              <span className="fa-cta-label">Speak to an Expert Now</span>
              <a href="tel:+919790050226" className="fa-cta-phone">+91 97900 50226</a>
            </div>
          </motion.div>

          <motion.div className="fa-divider-text" variants={itemVariants}>OR</motion.div>

          <motion.button
            className="fa-register-btn"
            onClick={scrollToContact}
            variants={itemVariants}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
          >
            Register Your Interest
            <span className="fa-btn-arrow">→</span>
          </motion.button>
        </motion.div>

        {/* Trust line */}
        <motion.p
          className="fa-trust-line"
          variants={itemVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          ✓ No cost &nbsp;&nbsp; ✓ No obligation &nbsp;&nbsp; ✓ Expert engineers &nbsp;&nbsp; ✓ Delivered within 5 business days
        </motion.p>
      </div>
    </section>
  );
};

export default FactoryAssessment;
